<template>
  <div class="auth-page">
    <!-- ═══════════ COLUMNA IZQUIERDA — formulario ═══════════ -->
    <main class="auth-main">
      <div class="auth-form-wrap">
        <!-- Brand minimal arriba -->
        <NuxtLink to="/" class="auth-brand">
          Comanda<span>.</span>
          <span class="auth-brand-back">← Volver al inicio</span>
        </NuxtLink>

        <!-- Pantalla de verificación de email -->
        <section v-if="emailEnviado" class="auth-verify">
          <div class="auth-verify-icon" aria-hidden="true">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
          </div>
          <h2>Te hemos mandado un enlace.</h2>
          <p class="auth-verify-email">{{ emailRegistrado }}</p>
          <p class="auth-verify-hint">Abre el correo y pulsa el enlace para activar tu cuenta. Si no aparece en tu bandeja, mira en spam o promociones — algunas veces se cuela.</p>
          <div class="auth-verify-actions">
            <button
              type="button"
              class="cp-auth-btn cp-auth-btn-secondary"
              :disabled="reenvioLoading || reenvioCooldown > 0"
              @click="reenviarConfirmacion"
            >
              {{ reenvioLoading ? 'Reenviando…' : reenvioCooldown > 0 ? `Reenviar en ${reenvioCooldown}s` : 'Reenviar correo' }}
            </button>
            <button
              type="button"
              class="cp-auth-link"
              @click="emailEnviado = false; emailRegistrado = ''"
            >
              Volver al inicio de sesión
            </button>
          </div>
        </section>

        <!-- Formulario -->
        <section v-else class="auth-form-section">
          <div class="auth-eyebrow">{{ isLogin ? 'Bienvenido de vuelta' : 'Founder Cohort · 20 plazas' }}</div>
          <h1 class="auth-title">
            <template v-if="isLogin">Tu salón te está esperando.</template>
            <template v-else>Empieza a tapar el agujero hoy.</template>
          </h1>
          <p class="auth-sub">
            <template v-if="isLogin">Entra y abre el turno. Tarda menos de un café.</template>
            <template v-else>Crea tu cuenta gratis. Sin tarjeta. Sin permanencia. 14 días para que decidas si te quedas.</template>
          </p>

          <form @submit.prevent="handleSubmit" class="auth-form">
            <!-- Nombre completo (solo registro) -->
            <div v-if="!isLogin" class="cp-auth-field">
              <label for="fullName">Tu nombre</label>
              <input
                id="fullName"
                v-model="form.fullName"
                type="text"
                required
                autocomplete="name"
                class="cp-auth-input"
                placeholder="ej. Juan García"
              />
              <p v-if="errors.fullName" class="cp-auth-error">{{ errors.fullName }}</p>
            </div>

            <!-- Email -->
            <div class="cp-auth-field">
              <label for="email">Correo</label>
              <input
                id="email"
                v-model="form.email"
                type="email"
                required
                autocomplete="email"
                class="cp-auth-input"
                placeholder="tu@email.com"
              />
              <p v-if="errors.email" class="cp-auth-error">{{ errors.email }}</p>
            </div>

            <!-- Contraseña -->
            <div class="cp-auth-field">
              <label for="password">
                Contraseña
                <button
                  v-if="isLogin"
                  type="button"
                  class="cp-auth-label-link"
                  @click="showForgotPassword = true"
                >¿Olvidada?</button>
              </label>
              <div class="cp-auth-input-wrap">
                <input
                  id="password"
                  v-model="form.password"
                  :type="showPassword ? 'text' : 'password'"
                  required
                  :autocomplete="isLogin ? 'current-password' : 'new-password'"
                  class="cp-auth-input cp-auth-input-pad"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  class="cp-auth-input-toggle"
                  :aria-label="showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                  @click="showPassword = !showPassword"
                >
                  <svg v-if="showPassword" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" stroke-linecap="round"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                    <path d="M3 3l18 18M10.6 10.6a2.99 2.99 0 0 0 4.2 4.2M9.4 5.2A10 10 0 0 1 22 12s-1 1.9-3 3.6M14.6 18.8A10 10 0 0 1 2 12s1-1.9 3-3.6" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
              <p v-if="errors.password" class="cp-auth-error">{{ errors.password }}</p>
              <p v-if="!isLogin && !errors.password" class="cp-auth-hint">Mínimo 8 caracteres con al menos una mayúscula y un número. Usa una que recuerdes.</p>
            </div>

            <!-- Confirmar contraseña (solo registro) -->
            <div v-if="!isLogin" class="cp-auth-field">
              <label for="confirmPassword">Repite la contraseña</label>
              <input
                id="confirmPassword"
                v-model="form.confirmPassword"
                type="password"
                required
                autocomplete="new-password"
                class="cp-auth-input"
                placeholder="••••••••"
              />
              <p v-if="errors.confirmPassword" class="cp-auth-error">{{ errors.confirmPassword }}</p>
            </div>

            <!-- Checkbox: recordar (login) o términos (registro) -->
            <label v-if="isLogin" class="cp-auth-check">
              <input
                id="remember-me"
                v-model="form.rememberMe"
                type="checkbox"
              />
              <span>Recordar mi sesión en este dispositivo</span>
            </label>

            <label v-else class="cp-auth-check">
              <input
                id="accept-terms"
                v-model="form.acceptTerms"
                type="checkbox"
                required
              />
              <span>
                Acepto los <NuxtLink to="/legal/terminos">términos</NuxtLink>
                y la <NuxtLink to="/legal/privacidad">privacidad</NuxtLink>.
                Hostelería es serio, lo nuestro también.
              </span>
            </label>

            <!-- Error general -->
            <div v-if="submitError" class="cp-auth-alert" role="alert">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span>{{ submitError }}</span>
            </div>

            <!-- Submit -->
            <button
              type="submit"
              class="cp-auth-btn cp-auth-btn-primary"
              :disabled="loading || !isFormValid"
            >
              <svg v-if="loading" class="cp-auth-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                <path d="M21 12a9 9 0 1 1-6.2-8.55" stroke-linecap="round"/>
              </svg>
              <span v-if="loading">{{ isLogin ? 'Entrando…' : 'Creando tu cuenta…' }}</span>
              <span v-else>{{ isLogin ? 'Entrar' : 'Crear mi cuenta gratis' }}</span>
            </button>

            <!-- Toggle login/registro -->
            <p class="cp-auth-toggle">
              {{ isLogin ? '¿Aún no tienes cuenta?' : '¿Ya tienes cuenta?' }}
              <button type="button" @click="toggleMode">
                {{ isLogin ? 'Crea una en 60 segundos' : 'Inicia sesión' }}
              </button>
            </p>
          </form>
        </section>

        <!-- Trust strip inferior — solo visible en form -->
        <ul v-if="!emailEnviado" class="auth-trust" aria-label="Garantías">
          <li>10 min para empezar</li>
          <li>Sin tarjeta</li>
          <li>Cancelas cuando quieras</li>
        </ul>
      </div>

      <!-- Modal recuperar contraseña -->
      <Teleport to="body">
        <Transition name="auth-modal">
          <div v-if="showForgotPassword" class="cp-auth-modal" @click.self="showForgotPassword = false" @keydown="onModalKeydown">
            <div
              ref="modalCardRef"
              class="cp-auth-modal-card"
              role="dialog"
              aria-modal="true"
              aria-labelledby="reset-title"
            >
              <div class="cp-auth-modal-head">
                <h3 id="reset-title">Recuperar contraseña</h3>
                <button
                  type="button"
                  class="cp-auth-modal-close"
                  aria-label="Cerrar"
                  @click="showForgotPassword = false"
                >×</button>
              </div>
              <form @submit.prevent="handleForgotPassword" class="cp-auth-modal-body">
                <p class="cp-auth-modal-text">Te mandamos un enlace al correo. Pulsas, eliges contraseña nueva, y a seguir.</p>
                <div class="cp-auth-field">
                  <label for="resetEmail">Tu correo</label>
                  <input
                    id="resetEmail"
                    ref="resetEmailInputRef"
                    v-model="resetEmail"
                    type="email"
                    required
                    autocomplete="email"
                    class="cp-auth-input"
                    placeholder="tu@email.com"
                  />
                </div>
                <div class="cp-auth-modal-actions">
                  <button type="button" class="cp-auth-btn cp-auth-btn-secondary" @click="showForgotPassword = false">
                    Cancelar
                  </button>
                  <button type="submit" class="cp-auth-btn cp-auth-btn-primary" :disabled="!resetEmail || resetLoading">
                    {{ resetLoading ? 'Enviando…' : 'Mandar enlace' }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Transition>
      </Teleport>
    </main>

    <!-- ═══════════ COLUMNA DERECHA — propuesta de valor (Hormozi) ═══════════ -->
    <aside class="auth-aside" aria-label="Por qué ComandaPlus">
      <div class="auth-aside-inner">
        <div class="auth-aside-eyebrow">Lo que ganas el lunes</div>
        <h2 class="auth-aside-title">
          <template v-if="isLogin">Ya conoces el sitio.<br>Te dejamos abrir.</template>
          <template v-else>7.000 €/año dejan de irse a la basura.</template>
        </h2>

        <ul class="auth-aside-points">
          <li>
            <span class="auth-aside-num">−30 min</span>
            <span class="auth-aside-text">cobrando cuentas divididas. Cada viernes noche.</span>
          </li>
          <li>
            <span class="auth-aside-num">−3.000 €</span>
            <span class="auth-aside-text">al año en descuadres invisibles. Cada año.</span>
          </li>
          <li>
            <span class="auth-aside-num">+15 %</span>
            <span class="auth-aside-text">de margen al cortar los platos peso muerto.</span>
          </li>
          <li>
            <span class="auth-aside-num">2027</span>
            <span class="auth-aside-text">cubierto. VeriFactu listo antes del plazo.</span>
          </li>
        </ul>

        <!-- Mini visual del producto -->
        <figure class="auth-aside-mock" aria-hidden="true">
          <svg viewBox="0 0 280 180" xmlns="http://www.w3.org/2000/svg">
            <rect x="0" y="0" width="280" height="180" rx="6" fill="#1A1A18"/>
            <rect x="14" y="14" width="252" height="20" rx="3" fill="#0B0B0A"/>
            <text x="24" y="28" font-size="9" font-weight="700" fill="#D14A2C" font-family="JetBrains Mono, monospace" letter-spacing="1">SALÓN · EN VIVO</text>
            <circle cx="256" cy="24" r="3" fill="#5B7F3C"/>
            <!-- Mesa cards mini -->
            <g transform="translate(14, 44)">
              <rect width="78" height="40" rx="3" fill="#fff" stroke="#5B7F3C"/>
              <text x="8" y="16" font-size="9" font-weight="700" fill="#0B0B0A" font-family="Geist, sans-serif">Mesa 1</text>
              <text x="8" y="28" font-size="7" fill="#5B7F3C" font-family="Geist, sans-serif">libre</text>

              <rect x="86" width="78" height="40" rx="3" fill="#fff" stroke="#0B0B0A" stroke-width="1.5"/>
              <text x="94" y="16" font-size="9" font-weight="700" fill="#D14A2C" font-family="Geist, sans-serif">Mesa 7 ●</text>
              <text x="94" y="28" font-size="7" fill="#0B0B0A" font-family="JetBrains Mono, monospace" font-weight="700">41,50 €</text>

              <rect x="172" width="78" height="40" rx="3" fill="#fff" stroke="#B8761F"/>
              <text x="180" y="16" font-size="9" font-weight="700" fill="#0B0B0A" font-family="Geist, sans-serif">Mesa 3</text>
              <text x="180" y="28" font-size="7" fill="#B8761F" font-family="Geist, sans-serif">cuenta</text>
            </g>
            <!-- KPIs mini -->
            <g transform="translate(14, 96)">
              <text x="0" y="10" font-size="7" fill="#B5B0A6" font-family="JetBrains Mono, monospace">OCUPACIÓN</text>
              <text x="0" y="28" font-size="20" font-weight="800" fill="#fff" font-family="Geist, sans-serif">66<tspan font-size="11" fill="#B5B0A6">%</tspan></text>

              <text x="120" y="10" font-size="7" fill="#B5B0A6" font-family="JetBrains Mono, monospace">VENTAS HOY</text>
              <text x="120" y="28" font-size="20" font-weight="800" fill="#fff" font-family="JetBrains Mono, monospace">487<tspan font-size="11" fill="#B5B0A6"> €</tspan></text>
            </g>
            <!-- Pulse notification -->
            <g transform="translate(14, 142)">
              <rect width="252" height="28" rx="3" fill="#0B0B0A" stroke="#D14A2C"/>
              <circle cx="14" cy="14" r="4" fill="#D14A2C">
                <animate attributeName="opacity" values="1;0.4;1" dur="1.6s" repeatCount="indefinite"/>
              </circle>
              <text x="26" y="12" font-size="8" font-weight="700" fill="#fff" font-family="Geist, sans-serif">Mesa 5 · pide cuenta</text>
              <text x="26" y="22" font-size="7" fill="#B5B0A6" font-family="JetBrains Mono, monospace">28,90 € · 2 comensales</text>
            </g>
          </svg>
        </figure>

        <p class="auth-aside-quote">
          “Restaurantes desde Málaga hasta Barcelona se niegan ya a dividir cuentas.”
          <cite>— Euro Weekly News, julio 2025</cite>
        </p>
      </div>
    </aside>
  </div>
</template>

<script setup lang="ts">
import { loginSchema, registerSchema } from '~/schemas/auth'

definePageMeta({ layout: 'public' })

const authStore = useAuthStore()
const { showToast } = useToast()
const route = useRoute()
const router = useRouter()

/**
 * ¿Login o registro al aterrizar?
 *
 * Quien viene de la landing ("Empezar gratis") NUNCA ha estado aquí: recibirle
 * con "Bienvenido de vuelta" es el peor saludo posible. La landing enlaza a
 * `/auth?modo=registro` (también vale el atajo `/auth?registro`) y cae directo
 * en el formulario de alta. Quien ya es cliente entra por `/auth` a secas y
 * sigue viendo el login de siempre.
 */
const MODO_REGISTRO = 'registro'
const quiereRegistro = (q: typeof route.query): boolean =>
  String(q.modo ?? '').toLowerCase() === MODO_REGISTRO || q.registro !== undefined

const isLogin = ref(!quiereRegistro(route.query))
const showPassword = ref(false)
const showForgotPassword = ref(false)
const loading = ref(false)
const resetLoading = ref(false)
const submitError = ref('')
const resetEmail = ref('')
const emailEnviado = ref(false)
const emailRegistrado = ref('')
const reenvioLoading = ref(false)
const reenvioCooldown = ref(0)

const form = ref({
  email: '',
  password: '',
  fullName: '',
  confirmPassword: '',
  rememberMe: false,
  acceptTerms: false
})

const errors = ref<Record<string, string>>({})

const isFormValid = computed(() => {
  if (isLogin.value) {
    return form.value.email && form.value.password
  }
  return form.value.email && form.value.password && form.value.fullName && form.value.confirmPassword && form.value.acceptTerms
})

const toggleMode = () => {
  isLogin.value = !isLogin.value
  errors.value = {}
  submitError.value = ''
  sincronizarUrlConModo()
}

/**
 * La URL refleja el modo. Así, si el hostelero recarga (o comparte el enlace,
 * o vuelve con el botón "atrás"), sigue donde estaba en vez de saltar al login.
 * `replace` para no llenar el historial con cada clic en el toggle.
 */
const sincronizarUrlConModo = () => {
  const query = { ...route.query }
  delete query.registro
  if (isLogin.value) delete query.modo
  else query.modo = MODO_REGISTRO
  router.replace({ query })
}

// Si el visitante llega ya con ?registro (atajo), normalizamos a ?modo=registro
// para que solo haya UNA forma canónica del enlace en circulación.
onMounted(() => {
  if (route.query.registro !== undefined) sincronizarUrlConModo()
})

const validateForm = () => {
  // M4: fuente de verdad ahora es schemas/auth.ts (Zod). Antes había regex
  // inline con min 6 caracteres mientras el schema exigía 8+mayúscula+número
  // → reglas distintas según el sitio.
  errors.value = {}
  if (isLogin.value) {
    const result = loginSchema.safeParse({
      email: form.value.email,
      password: form.value.password,
    })
    if (!result.success) {
      for (const issue of result.error.issues) {
        const field = issue.path[0] as string
        if (field && !errors.value[field]) errors.value[field] = issue.message
      }
    }
  } else {
    const result = registerSchema.safeParse({
      email: form.value.email,
      password: form.value.password,
      confirmPassword: form.value.confirmPassword,
      nombre_completo: form.value.fullName.trim(),
      acceptTerms: form.value.acceptTerms,
    })
    if (!result.success) {
      for (const issue of result.error.issues) {
        const rawField = issue.path[0] as string
        // El form usa "fullName" pero el schema "nombre_completo".
        const field = rawField === 'nombre_completo' ? 'fullName' : rawField
        if (field && !errors.value[field]) errors.value[field] = issue.message
      }
    }
  }
  return Object.keys(errors.value).length === 0
}

const handleSubmit = async () => {
  if (!validateForm()) return

  try {
    loading.value = true
    submitError.value = ''

    if (isLogin.value) {
      await authStore.signIn(form.value.email, form.value.password)
      showToast('Sesión iniciada correctamente', 'success')

      // Esperar a que el perfil y restaurantes terminen de cargar
      await authStore.waitForProfileReady()

      // Redirigir dinámicamente
      const restaurantId = authStore.currentRestaurant?.id
      if (restaurantId) {
        return await navigateTo(`/dashboard/${restaurantId}`)
      }
      if (authStore.hasProfile) {
        // Multi-tenant sin selección previa → picker de restaurantes
        if (authStore.userRestaurants.length >= 2) {
          return await navigateTo('/elegir-restaurante')
        }
        return await navigateTo('/')
      }
      return await navigateTo('/create-profile')
    } else {
      // Registro
      const result = await authStore.signUp(form.value.email, form.value.password, {
        full_name: form.value.fullName
      })

      // Mínima fricción para campaña: si la confirmación de email está
      // DESACTIVADA en Supabase, signUp devuelve ya una sesión activa →
      // entramos directos al flujo sin pasar por "revisa tu correo".
      // Si la confirmación sigue ACTIVA, session es null y mostramos la
      // pantalla de verificación como siempre. Así el flujo funciona bien
      // con el interruptor en cualquiera de las dos posiciones.
      if (result?.session) {
        showToast('Cuenta creada. ¡Bienvenido!', 'success')
        await authStore.waitForProfileReady()
        return await navigateTo('/create-profile')
      }

      // Confirmación de email activa → pantalla de verificación
      emailRegistrado.value = form.value.email
      emailEnviado.value = true
      startReenvioCooldown()
    }
  } catch (error: any) {
    console.error('Auth error:', error)
    // Fix REGISTER-#4: traducir errores típicos de Supabase Auth a mensajes humanos.
    // Supabase devuelve 422 con "User already registered" para emails duplicados,
    // y 400 con "Invalid login credentials" para password errado. Antes salía el
    // mensaje técnico crudo, generando confusión.
    const msg = error instanceof Error ? error.message : ''
    const status = error?.status
    if (!isLogin.value && (status === 422 || /already registered|user.+exists/i.test(msg))) {
      submitError.value = 'Este correo ya está registrado. Inicia sesión o usa "Recuperar contraseña".'
    } else if (isLogin.value && /invalid login credentials/i.test(msg)) {
      submitError.value = 'Correo o contraseña incorrectos.'
    } else if (/email.+confirm|not confirmed/i.test(msg)) {
      submitError.value = 'Confirma tu correo desde el email que te enviamos antes de iniciar sesión.'
    } else if (/network|fetch failed/i.test(msg)) {
      submitError.value = 'Sin conexión. Revisa tu internet e intenta de nuevo.'
    } else {
      submitError.value = msg || 'Error de autenticación. Intenta de nuevo.'
    }
  } finally {
    loading.value = false
  }
}

function startReenvioCooldown() {
  reenvioCooldown.value = 60
  const interval = setInterval(() => {
    reenvioCooldown.value--
    if (reenvioCooldown.value <= 0) clearInterval(interval)
  }, 1000)
}

const reenviarConfirmacion = async () => {
  if (!emailRegistrado.value) return
  try {
    reenvioLoading.value = true
    const client = useSupabaseClient()
    const { error } = await client.auth.resend({
      type: 'signup',
      email: emailRegistrado.value,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/confirm`
      }
    })
    if (error) throw error
    showToast('Email de verificación reenviado', 'success')
    startReenvioCooldown()
  } catch (err: any) {
    reportarError(err, { scope: 'auth.reenviarVerificacion' })
    showToast('No se pudo reenviar el correo. Reintenta en un minuto.', 'error')
  } finally {
    reenvioLoading.value = false
  }
}

// m7: focus trap básico en el modal de recuperar contraseña.
// Cuando se abre, foco al primer input. Tab/Shift+Tab ciclan dentro
// del modal. Escape lo cierra.
const modalCardRef = ref<HTMLElement | null>(null)
const resetEmailInputRef = ref<HTMLInputElement | null>(null)

watch(showForgotPassword, (open) => {
  if (open) {
    nextTick(() => resetEmailInputRef.value?.focus())
  }
})

function onModalKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    showForgotPassword.value = false
    return
  }
  if (e.key !== 'Tab' || !modalCardRef.value) return
  const focusables = modalCardRef.value.querySelectorAll<HTMLElement>(
    'button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
  )
  if (focusables.length === 0) return
  const first = focusables[0]
  const last = focusables[focusables.length - 1]
  const active = document.activeElement as HTMLElement | null
  if (e.shiftKey && active === first) {
    e.preventDefault()
    last.focus()
  } else if (!e.shiftKey && active === last) {
    e.preventDefault()
    first.focus()
  }
}

const handleForgotPassword = async () => {
  if (!resetEmail.value) return
  try {
    resetLoading.value = true
    await authStore.resetPassword(resetEmail.value)
    showToast('Email de recuperación enviado', 'success')
    showForgotPassword.value = false
    resetEmail.value = ''
  } catch (error) {
    console.error('Reset password error:', error)
    showToast('No se pudo enviar el email. Verifica tu dirección e intenta de nuevo.', 'error')
  } finally {
    resetLoading.value = false
  }
}
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════════════
   AUTH — login + registro · sistema D1 Operational + Hormozi
   Layout split desktop, single-column móvil. Lado izq formulario,
   lado dch propuesta de valor con cifras y mockup mini del producto.
   ═══════════════════════════════════════════════════════════════════ */

.auth-page {
  display: grid;
  grid-template-columns: 1fr;
  min-height: 100vh;
  background: #F7F6F2;
  font-family: 'Geist', 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  color: #0B0B0A;
}

@media (min-width: 1024px) {
  .auth-page {
    grid-template-columns: 1.05fr 0.95fr;
  }
}

/* ═════════ COLUMNA IZQUIERDA — formulario ═════════ */
.auth-main {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1.5rem;
  background: #F7F6F2;
}
.auth-form-wrap {
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

/* Brand minimal arriba */
.auth-brand {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 1.4rem;
  font-weight: 800;
  color: #0B0B0A;
  text-decoration: none;
  letter-spacing: -0.02em;
}
.auth-brand span {
  color: #D14A2C;
}
.auth-brand-back {
  font-size: 0.82rem;
  font-weight: 500;
  color: #6B6862;
  letter-spacing: 0;
}
.auth-brand:hover .auth-brand-back { color: #0B0B0A; }

/* Eyebrow + titulares */
.auth-eyebrow {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #D14A2C;
  font-weight: 600;
  margin-bottom: 0.85rem;
}
.auth-title {
  font-size: clamp(1.6rem, 3.2vw, 2.1rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
  margin: 0 0 0.65rem 0;
  color: #0B0B0A;
}
.auth-sub {
  font-size: 0.96rem;
  color: #3F1A0F;
  line-height: 1.55;
  margin: 0 0 2rem 0;
}

/* Form */
.auth-form {
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}

.cp-auth-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.cp-auth-field label {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 10.5px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #6B6862;
  font-weight: 500;
}
.cp-auth-label-link {
  background: none;
  border: 0;
  padding: 0;
  font-family: 'Geist', sans-serif;
  font-size: 0.78rem;
  color: #D14A2C;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
  cursor: pointer;
}
.cp-auth-label-link:hover { color: #A8391F; text-decoration: underline; }

.cp-auth-input {
  width: 100%;
  background: #fff;
  border: 1px solid #E4E2DC;
  border-radius: 4px;
  font-family: 'Geist', sans-serif;
  /* 16px NO ES NEGOCIABLE. Safari en iPhone hace zoom automático sobre
     cualquier campo con menos de 16px: el hostelero toca "Correo" y la pantalla
     le da un salto justo cuando va a escribir. Es el último paso del embudo.
     Si algún día se quiere el texto más pequeño, se hace con `transform`, nunca
     bajando el font-size. */
  font-size: 16px;
  color: #0B0B0A;
  padding: 12px 14px;
  outline: none;
  transition: border-color 100ms ease, box-shadow 100ms ease;
  -webkit-appearance: none;
  appearance: none;
  /* Touch target ≥ 44px */
  min-height: 44px;
}
.cp-auth-input:focus {
  border-color: #0B0B0A;
  box-shadow: 0 0 0 3px rgba(11, 11, 10, 0.08);
}
.cp-auth-input::placeholder {
  color: #B5B0A6;
}

/* Input con botón de toggle (password) */
.cp-auth-input-wrap {
  position: relative;
}
.cp-auth-input-pad {
  padding-right: 44px;
}
.cp-auth-input-toggle {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  background: transparent;
  border: 0;
  border-radius: 4px;
  color: #6B6862;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.cp-auth-input-toggle:hover {
  background: #E4E2DC;
  color: #0B0B0A;
}

.cp-auth-error {
  margin: 0;
  font-size: 0.82rem;
  color: #D14A2C;
  font-weight: 600;
}
.cp-auth-hint {
  margin: 0;
  font-size: 0.78rem;
  color: #6B6862;
}

/* Checkbox */
.cp-auth-check {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.88rem;
  color: #3F1A0F;
  cursor: pointer;
  line-height: 1.45;
}
.cp-auth-check input[type="checkbox"] {
  appearance: none;
  -webkit-appearance: none;
  width: 18px;
  height: 18px;
  border: 1.5px solid #6B6862;
  border-radius: 3px;
  cursor: pointer;
  flex-shrink: 0;
  margin-top: 1px;
  position: relative;
  background: #fff;
  transition: background 100ms ease, border-color 100ms ease;
}
.cp-auth-check input[type="checkbox"]:checked {
  background: #0B0B0A;
  border-color: #0B0B0A;
}
.cp-auth-check input[type="checkbox"]:checked::after {
  content: "";
  position: absolute;
  left: 5px;
  top: 1px;
  width: 5px;
  height: 10px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.cp-auth-check a {
  color: #0B0B0A;
  text-decoration: underline;
  font-weight: 500;
}
.cp-auth-check a:hover { color: #D14A2C; }

/* Alert error */
.cp-auth-alert {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #FBE4DE;
  border: 1px solid #D14A2C;
  border-radius: 4px;
  padding: 12px 14px;
  color: #A8391F;
  font-size: 0.88rem;
  font-weight: 500;
  line-height: 1.45;
}
.cp-auth-alert svg {
  flex-shrink: 0;
  margin-top: 1px;
}

/* Botones */
.cp-auth-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 13px 18px;
  border-radius: 4px;
  font-family: 'Geist', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: -0.005em;
  cursor: pointer;
  transition: background 120ms ease, transform 80ms ease, opacity 100ms ease;
  border: 1px solid #0B0B0A;
  min-height: 48px;
}
.cp-auth-btn-primary {
  background: #0B0B0A;
  color: #F7F6F2;
}
.cp-auth-btn-primary:hover:not(:disabled) {
  background: #2A2A28;
  transform: translateY(-1px);
}
.cp-auth-btn-primary:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.cp-auth-btn-secondary {
  background: transparent;
  color: #0B0B0A;
}
.cp-auth-btn-secondary:hover:not(:disabled) {
  background: #E4E2DC;
}

.cp-auth-spin {
  animation: auth-spin 700ms linear infinite;
}
@keyframes auth-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Toggle login/registro */
.cp-auth-toggle {
  margin: 0.25rem 0 0 0;
  text-align: center;
  font-size: 0.88rem;
  color: #6B6862;
}
.cp-auth-toggle button {
  background: none;
  border: 0;
  padding: 0;
  color: #D14A2C;
  font-weight: 700;
  font-family: inherit;
  font-size: inherit;
  cursor: pointer;
  margin-left: 0.35rem;
}
.cp-auth-toggle button:hover {
  color: #A8391F;
  text-decoration: underline;
}

/* Trust strip inferior */
.auth-trust {
  list-style: none;
  margin: 0;
  padding: 1rem 0 0 0;
  border-top: 1px solid #E4E2DC;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  color: #6B6862;
}
.auth-trust li::before {
  content: "✓ ";
  color: #5B7F3C;
  font-weight: 700;
}

.cp-auth-link {
  background: none;
  border: 0;
  padding: 0.5rem;
  color: #6B6862;
  font-size: 0.88rem;
  cursor: pointer;
  font-family: inherit;
}
.cp-auth-link:hover { color: #0B0B0A; }

/* ═════════ Pantalla verificación email ═════════ */
.auth-verify {
  text-align: center;
  padding: 2rem 0;
}
.auth-verify-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(209, 74, 44, 0.12);
  color: #D14A2C;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
}
.auth-verify h2 {
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0 0 0.75rem 0;
}
.auth-verify-email {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 0.95rem;
  font-weight: 700;
  color: #0B0B0A;
  margin: 0 0 1rem 0;
}
.auth-verify-hint {
  font-size: 0.88rem;
  color: #3F1A0F;
  line-height: 1.55;
  max-width: 360px;
  margin: 0 auto 1.5rem;
}
.auth-verify-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
}
.auth-verify-actions .cp-auth-btn {
  min-width: 240px;
}

/* ═════════ COLUMNA DERECHA — propuesta de valor ═════════ */
.auth-aside {
  background: #0B0B0A;
  color: #F7F6F2;
  padding: 3rem 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
@media (max-width: 1023px) {
  .auth-aside {
    /* En móvil aparece debajo del formulario */
    border-top: 1px solid #1A1A18;
  }
}

.auth-aside-inner {
  width: 100%;
  max-width: 440px;
}
.auth-aside-eyebrow {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #D14A2C;
  font-weight: 600;
  margin-bottom: 1rem;
}
.auth-aside-title {
  font-size: clamp(1.5rem, 2.8vw, 2rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
  margin: 0 0 2rem 0;
  color: #F7F6F2;
}

/* Lista de cifras de impacto */
.auth-aside-points {
  list-style: none;
  margin: 0 0 2rem 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  border-top: 1px solid #1A1A18;
}
.auth-aside-points li {
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 1.25rem;
  padding: 1rem 0;
  border-bottom: 1px solid #1A1A18;
  align-items: baseline;
}
.auth-aside-num {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 1.5rem;
  font-weight: 800;
  color: #D14A2C;
  letter-spacing: -0.01em;
  font-feature-settings: "tnum";
  font-variant-numeric: tabular-nums;
  line-height: 1;
}
.auth-aside-text {
  font-size: 0.95rem;
  color: #B5B0A6;
  line-height: 1.45;
}

/* Mockup mini del producto */
.auth-aside-mock {
  margin: 1.5rem 0;
}
.auth-aside-mock svg {
  width: 100%;
  height: auto;
  border-radius: 4px;
  filter: drop-shadow(0 8px 24px rgba(0, 0, 0, 0.35));
}

/* Quote */
.auth-aside-quote {
  font-style: italic;
  color: #B5B0A6;
  border-left: 3px solid #D14A2C;
  padding: 0.5rem 0 0.5rem 1rem;
  font-size: 0.92rem;
  line-height: 1.5;
  margin: 1.5rem 0 0 0;
}
.auth-aside-quote cite {
  display: block;
  font-style: normal;
  font-size: 0.78rem;
  color: #6B6862;
  margin-top: 0.5rem;
  letter-spacing: 0.02em;
}

/* ═════════ Modal recuperar contraseña ═════════ */
.cp-auth-modal {
  position: fixed;
  inset: 0;
  background: rgba(11, 11, 10, 0.6);
  backdrop-filter: blur(6px);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
}
.cp-auth-modal-card {
  width: 100%;
  max-width: 420px;
  background: #F7F6F2;
  border: 1px solid #0B0B0A;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(11, 11, 10, 0.35);
}
.cp-auth-modal-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #E4E2DC;
  background: #fff;
}
.cp-auth-modal-head h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: -0.01em;
}
.cp-auth-modal-close {
  background: transparent;
  border: 0;
  font-size: 1.6rem;
  line-height: 1;
  color: #6B6862;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  border-radius: 4px;
}
.cp-auth-modal-close:hover {
  background: #E4E2DC;
  color: #0B0B0A;
}
.cp-auth-modal-body {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.cp-auth-modal-text {
  margin: 0;
  font-size: 0.9rem;
  color: #3F1A0F;
  line-height: 1.5;
}
.cp-auth-modal-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
}
.cp-auth-modal-actions .cp-auth-btn {
  flex: 1;
}

/* Transition */
.auth-modal-enter-active,
.auth-modal-leave-active {
  transition: opacity 180ms ease;
}
.auth-modal-enter-from,
.auth-modal-leave-to {
  opacity: 0;
}
.auth-modal-enter-active .cp-auth-modal-card,
.auth-modal-leave-active .cp-auth-modal-card {
  transition: transform 200ms ease;
}
.auth-modal-enter-from .cp-auth-modal-card {
  transform: scale(0.96);
}

/* Responsive móvil */
@media (max-width: 767px) {
  .auth-main { padding: 1.5rem 1.25rem; }
  .auth-aside { padding: 2.5rem 1.5rem; }
  .auth-aside-points li {
    grid-template-columns: 90px 1fr;
    gap: 1rem;
  }
  .auth-aside-num { font-size: 1.3rem; }
  .auth-trust {
    font-size: 10px;
    flex-direction: column;
    gap: 0.4rem;
  }
}
</style>
